import React from 'react';
import { Box, Stack, Typography, Tooltip } from '@mui/material';

// กลุ่มสถานะ radio-style — คลิกเพื่อเลือก/ยกเลิก (เหมือนแท็บ "สถานภาพ" เดิม)
// warnEmpty: ขึ้นแดงเตือนถ้ายังไม่ได้เลือกอะไรเลย (currentKey ว่าง) — ใช้กับช่องที่ต้องบังคับให้ user ตัดสินใจ
// dangerBg: บังคับกล่องทั้งใบ (กรอบ/แถบข้าง/ตัวหนา/พื้นหลัง) เป็นสีแดงแทนสีเฉพาะของแต่ละกลุ่ม
// (blue/orange/purple ฯลฯ) — ใช้ตอนโปรเจกต์มีคดีฟ้องร้อง ให้ทั้ง sidebar เป็นสีแดงเป็นเนื้อเดียวกัน
// ไม่ใช่แค่พื้นหลังรอบนอกกล่อง (ของเดิมกล่องแต่ละใบทึบสีตัวเองทับพื้นหลังหน้าไว้อยู่แล้ว)
// boxTone: { color, bg } บังคับสีกล่องทั้งใบตามสถานะที่เลือก (เช่น PO: ไม่มี PO = แดง, รอ PO = ส้ม) ชนะสีกลุ่มปกติ
// pairedKeys: key ของตัวเลือกที่ชื่อสั้น ให้วางเคียงกัน 2 ช่องในแถวเดียว (ที่เหลือเรียงลงมาเต็มความกว้างเหมือนเดิม)
export function StatusGroup({ title, color, options, currentKey, onSelect, warnEmpty, getOptionDisabledReason, dangerBg, boxTone, pairedKeys = [] }) {
  const isEmpty = warnEmpty && !currentKey;
  const effectiveColor = boxTone?.color || (isEmpty || dangerBg ? '#dc2626' : color);

  const renderOption = (opt, compact) => {
    const selected = currentKey === opt.key;
    const disabledReason = getOptionDisabledReason?.(opt, { selected, currentKey }) || '';
    const disabled = Boolean(disabledReason);
    const optionNode = (
      <Box
        key={opt.key}
        onClick={() => {
          if (disabled) return;
          onSelect(selected ? '' : opt.key);
        }}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.6,
          py: 0.1,
          px: 0.5,
          minWidth: 0,
          borderRadius: 1,
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.55 : 1,
          bgcolor: selected ? `${opt.color}18` : 'transparent',
          '&:hover': { bgcolor: disabled ? 'transparent' : `${opt.color}10` },
        }}
      >
        <Box
          sx={{
            width: 12,
            height: 12,
            borderRadius: '50%',
            flexShrink: 0,
            border: '2px solid',
            borderColor: selected ? opt.color : '#cbd5e1',
            bgcolor: selected ? opt.color : 'transparent',
          }}
        />
        <Typography
          variant="caption"
          sx={{
            fontSize: '0.72rem',
            color: selected ? opt.color : 'text.primary',
            fontWeight: selected ? 700 : 400,
            // ช่องคู่แคบ ห้ามตัวอักษรล้นไปทับช่องข้างๆ — ตัดด้วย ... แทน
            ...(compact && { minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }),
          }}
        >
          {opt.label}
        </Typography>
      </Box>
    );

    if (disabled) {
      return (
        <Tooltip key={opt.key} title={disabledReason} placement="top">
          <Box component="span" sx={{ display: 'block', minWidth: 0 }}>
            {optionNode}
          </Box>
        </Tooltip>
      );
    }

    return <React.Fragment key={opt.key}>{optionNode}</React.Fragment>;
  };

  const pairedOptions = options.filter((o) => pairedKeys.includes(o.key));
  const restOptions = options.filter((o) => !pairedKeys.includes(o.key));

  return (
    <Box
      sx={{
        p: 0.65,
        borderRadius: 1.5,
        border: '1px solid',
        borderColor: `${effectiveColor}${isEmpty ? '80' : '28'}`,
        boxShadow: `inset 3px 0 0 ${effectiveColor}`,
        bgcolor: boxTone?.bg || (isEmpty || dangerBg ? '#fef2f2' : '#fafbff'),
        mb: 0.6,
      }}
    >
      <Typography
        variant="caption"
        sx={{
          display: 'block',
          fontWeight: 700,
          fontSize: '0.67rem',
          color: effectiveColor,
          mb: 0.3,
          pb: 0.15,
          borderBottom: `1px solid ${effectiveColor}25`,
        }}
      >
        {title}
        {isEmpty && ' ⚠ กรุณาเลือก'}
      </Typography>
      <Stack spacing={0}>
        {pairedOptions.length > 0 && (
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: 0.25 }}>
            {pairedOptions.map((opt) => renderOption(opt, true))}
          </Box>
        )}
        {restOptions.map((opt) => renderOption(opt, false))}
      </Stack>
    </Box>
  );
}
